import { css } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, IconButton, useTheme } from "@mui/material";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { match } from "ts-pattern";

import LimitedContentArea, {
  FanLimitedContent,
  LimitedContent,
  MembershipLimitedContent,
} from "~/components/category/[id]/limited/LimitedContentArea";
import { mediaQueryIsMobile } from "~/lib/client/design";
import { useGraphqlClientWithAuth } from "~/lib/client/hooks/useGraphqlClient";
import { MeEligibleLimitationQuery } from "~/lib/graphql/client";
import { useCategoryDetailContext } from "~/pages/category/[id]";

const LimitedTab = () => {
  return (
    <div css={rootStyle}>
      <LimitedContentContainer />
    </div>
  );
};

const rootStyle = css`
  display: flex;
  width: 100%;
  max-width: 640px;
  margin: 4rem auto;

  ${mediaQueryIsMobile(css`
    margin-top: 2rem;
    padding: 0 1rem;
  `)}
`;

const HOW_TO_GET_FAN_LINK = "/ad/04893334416";

const LimitedContentContainer = () => {
  const theme = useTheme();
  const router = useRouter();
  const [meEligibleLimitedContents, setMeEligibleLimitedContents] = useState<{
    membershipIds: number[];
    attestationIds: number[];
    fanLimitedContents: MeEligibleLimitationQuery["fanLimitedContents"];
  }>();
  const client = useGraphqlClientWithAuth();
  const { props } = useCategoryDetailContext();
  const [focusedLimitedContent, setFocusedLimitedContent] =
    useState<LimitedContent | null>(null);

  const fanLimited: FanLimitedContent[] = props.category.fanLimitedContents.map(
    (c): FanLimitedContent => ({
      ...c,
      isEligible: (ids: number[]) =>
        c.fussyAttestations.some((a) => ids.includes(a.id)),
      __typename: "FanLimitedContent" as const,
      requiredDialogBody: (
        <div css={dialogBodyStyle}>
          <div>このコンテンツを見るには次のFANが必要です。</div>
          <div css={attestationsContainerStyle}>
            {c.fussyAttestations.map((a) => (
              <div key={a.id} css={attestationLinkStyle}>
                <div id="title">{a.fussyAttestationEvent.title}</div>
                <Link id="link" href={HOW_TO_GET_FAN_LINK}>
                  取得方法
                </Link>
              </div>
            ))}
          </div>
        </div>
      ),
    })
  );
  const membershipLimited: MembershipLimitedContent[] =
    props.category.membershipLimitedContents.map(
      (c): MembershipLimitedContent => ({
        ...c,
        isEligible: (ids: number[]) =>
          c.visiblePartnerCategoryMemberships.some((m) => ids.includes(m.id)),
        __typename: "MembershipLimitedContent" as const,
        requiredDialogBody: (
          <div css={dialogBodyStyle}>
            <p>
              このコンテンツを見るには次のメンバーシップへの参加が必要です。
            </p>
            {c.visiblePartnerCategoryMemberships.map((m) => (
              <Link
                href={`/category/${props.category.id}/membership/${m.id}`}
                passHref
                key={m.id}
              >
                {m.title}
              </Link>
            ))}
          </div>
        ),
      })
    );

  const limitedContents = [...fanLimited, ...membershipLimited].toSorted(
    (a, b) => b.createdAt.localeCompare(a.createdAt)
  );

  const handleLimitedContentClick = useCallback(
    async (limitedContent: LimitedContent) => {
      return match(limitedContent)
        .with(
          { __typename: "FanLimitedContent" },
          async (fanLimitedContent) => {
            if (
              fanLimitedContent.isEligible(
                meEligibleLimitedContents?.attestationIds ?? []
              )
            ) {
              await router.push(`/notification`);
            } else {
              setFocusedLimitedContent(fanLimitedContent);
            }
          }
        )
        .with(
          { __typename: "MembershipLimitedContent" },
          async (membershipLimitedContent) => {
            if (
              membershipLimitedContent.isEligible(
                meEligibleLimitedContents?.membershipIds ?? []
              )
            ) {
              await router.push(`/category/${props.category.id}/membership`);
            } else {
              setFocusedLimitedContent(membershipLimitedContent);
            }
          }
        )
        .exhaustive();
    },
    [meEligibleLimitedContents, props.category.id, router]
  );

  useEffect(() => {
    if (!client) return;

    client
      .meEligibleLimitation({ categoryId: props.category.id })
      .then(
        ({
          me: { enabledMemberships, fussyAttestationCards },
          fanLimitedContents,
        }) => {
          const data = {
            membershipIds: enabledMemberships.map(
              (m) => m.partnerCategoryMembership.id
            ),
            attestationIds: fussyAttestationCards.map(
              (a) => a.fussyAttestation.id
            ),
            fanLimitedContents,
          };

          setMeEligibleLimitedContents(data);
        }
      );
  }, [client, props.category.id]);

  if (limitedContents.length === 0) {
    return <p>まだ投稿がありません</p>;
  }

  return (
    <div css={containerRootStyle}>
      {limitedContents.map((limitedContent, idx) => (
        <LimitedContentArea
          key={idx}
          limitedContent={limitedContent}
          handleLimitedContentClick={handleLimitedContentClick}
        />
      ))}
      <Dialog
        open={focusedLimitedContent !== null}
        onClose={() => setFocusedLimitedContent(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            width: "100%",
            height: "30dvh",
            m: 1,
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        <DialogContent>
          <IconButton onClick={() => setFocusedLimitedContent(null)}>
            <CloseIcon sx={{ color: theme.palette.text.primary }} />
          </IconButton>
          {focusedLimitedContent?.requiredDialogBody}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const containerRootStyle = css`
  display: flex;
  flex-flow: column;
  gap: 1rem;
  width: 100%;
  flex: 1;
`;

const dialogBodyStyle = css`
  a {
    text-decoration: underline;
  }
`;

const attestationsContainerStyle = css`
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const attestationLinkStyle = css`
  #title {
    display: inline;
    font-weight: 600;
    margin-right: 0.5rem;
  }

  #link {
    text-decoration: underline;
  }
`;

export default LimitedTab;
